import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Loader2, ArrowLeft, ArrowRight, CheckCircle2, ShieldAlert, FlaskConical,
  Sparkles, MapPin, AlertTriangle, HelpCircle, RefreshCw, Heart, Wallet, Clock, GitCompare,
} from "lucide-react";
import { toast } from "sonner";

const INTEREST_SCALE = [
  [1, "Sangat tidak tertarik"], [2, "Kurang tertarik"], [3, "Cukup tertarik"], [4, "Tertarik"], [5, "Sangat tertarik"],
];
const FIT_SCALE = [
  [1, "Sangat tidak sesuai"], [2, "Kurang sesuai"], [3, "Cukup sesuai"], [4, "Sesuai"], [5, "Sangat sesuai"],
];

const PERSONAL_QUESTIONS = [
  { key: "interest_score", label: "Seberapa tertarik Anda menjalankan bisnis ini?", scale: INTEREST_SCALE },
  { key: "capital_score", label: "Seberapa nyaman kebutuhan modal bisnis ini dengan kondisi keuangan Anda?", scale: FIT_SCALE },
  { key: "skill_time_score", label: "Seberapa yakin Anda punya waktu & kemampuan untuk menjalankan bisnis ini?", scale: FIT_SCALE },
];

const METRICS = [
  ["market_demand", "Potensi Demand"],
  ["target_customer_clarity", "Kejelasan Target Customer"],
  ["competition", "Persaingan"],
  ["differentiation", "Diferensiasi"],
  ["capital_efficiency", "Efisiensi Modal"],
  ["margin_potential", "Potensi Margin"],
  ["operational_complexity", "Kompleksitas Operasional"],
  ["speed_to_revenue", "Kecepatan Pendapatan Pertama"],
  ["repeat_order", "Repeat Order"],
  ["customer_acquisition_difficulty", "Kesulitan Dapat Customer"],
  ["scalability", "Skalabilitas"],
];

const GOOD = "bg-brand/15 text-brand";
const MID = "bg-brand-amber/15 text-brand-amber";
const LOW = "bg-slate-200 text-slate-600";

function toneFor(field, value) {
  const higherBetter = ["market_demand", "target_customer_clarity", "differentiation", "capital_efficiency", "margin_potential", "repeat_order", "scalability"];
  if (higherBetter.includes(field)) return { Tinggi: GOOD, Sedang: MID, Rendah: LOW }[value] || MID;
  if (field === "competition") return { Rendah: GOOD, Sedang: MID, Tinggi: LOW }[value] || MID;
  if (field === "operational_complexity") return { Ringan: GOOD, Sedang: MID, Tinggi: LOW }[value] || MID;
  if (field === "speed_to_revenue") return { Cepat: GOOD, Sedang: MID, "Lebih Lama": LOW }[value] || MID;
  if (field === "customer_acquisition_difficulty") return { Mudah: GOOD, Sedang: MID, Sulit: LOW }[value] || MID;
  return MID;
}

const FIT_LABEL_STYLE = {
  "Sangat cocok secara personal": "bg-brand text-white",
  "Cukup cocok secara personal": "bg-brand-accent text-white",
  "Perlu dipertimbangkan": "bg-brand-amber text-white",
};

export default function Validation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [val, setVal] = useState(null);
  const [phase, setPhase] = useState("personal"); // personal | deepPrompt | generating | results
  const [scores, setScores] = useState([{}, {}]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/validation");
        if (!data || data.ideas?.length !== 2) {
          navigate("/ideas");
          return;
        }
        setVal(data);
        if (data.personal) {
          setScores([
            { ...data.personal["0"] },
            { ...data.personal["1"] },
          ]);
        }
        if (data.status === "completed" && data.deep) setPhase("results");
        else if (data.personal) setPhase("deepPrompt");
        else setPhase("personal");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const setScore = (ideaIdx, key, value) =>
    setScores((prev) => prev.map((s, i) => (i === ideaIdx ? { ...s, [key]: value } : s)));

  const personalComplete = scores.every((s) =>
    PERSONAL_QUESTIONS.every((q) => s[q.key] >= 1 && s[q.key] <= 5));

  const submitPersonal = async () => {
    if (!personalComplete) { setError("Mohon nilai ketiga faktor untuk kedua ide."); return; }
    setSaving(true);
    setError("");
    try {
      const payload = { scores: scores.map((s) => ({ interest_score: s.interest_score, capital_score: s.capital_score, skill_time_score: s.skill_time_score })) };
      const { data } = await api.put("/validation/personal", payload);
      setVal(data);
      setPhase("deepPrompt");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan penilaian.");
    } finally {
      setSaving(false);
    }
  };

  const runDeep = async () => {
    setPhase("generating");
    setError("");
    try {
      const { data } = await api.post("/validation/deep");
      setVal(data);
      setPhase("results");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // Personal answers are already saved server-side — offer a friendly retry.
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menjalankan validasi mendalam.");
      setPhase("deepPrompt");
      toast.error("Gagal menjalankan validasi. Coba lagi.");
    }
  };

  if (loading) {
    return <DashboardLayout><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-accent" /></div></DashboardLayout>;
  }
  if (!val) return null;
  const ideas = val.ideas;

  // ---------- GENERATING ----------
  if (phase === "generating") {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-xl text-center py-20 animate-fade-up" data-testid="deep-generating">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Loader2 className="h-8 w-8 animate-spin" /></div>
          <h2 className="mt-6 font-heading text-xl font-bold text-ink">Menjalankan validasi mendalam…</h2>
          <p className="mt-3 text-ink-soft leading-relaxed">IDEbisnis AI sedang mengevaluasi kedua ide berdasarkan profil dan penilaian personal Anda. Ini bisa memakan waktu hingga satu menit.</p>
        </div>
      </DashboardLayout>
    );
  }

  // ---------- PERSONAL ----------
  if (phase === "personal") {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl animate-fade-up pb-28" data-testid="personal-validation">
          <button onClick={() => navigate("/ideas")} className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-brand transition-colors" data-testid="back-to-ideas">
            <ArrowLeft className="h-4 w-4" /> Ubah Pilihan
          </button>
          <h1 className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Validasi Personal</h1>
          <p className="mt-3 text-base text-ink-soft leading-relaxed">Sekarang nilai seberapa cocok kedua ide ini dengan kondisi dan preferensi Anda. Tidak ada jawaban benar atau salah.</p>

          {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="personal-error">{error}</div>}

          <div className="mt-6 space-y-6">
            {ideas.map((idea, idx) => (
              <div key={idx} className="rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid={`personal-idea-${idx}`}>
                <span className="font-mono text-xs font-semibold text-brand-accent">IDE #{idx + 1}</span>
                <h3 className="mt-1 font-heading text-lg font-bold text-ink">{idea.name}</h3>
                <div className="mt-5 space-y-5">
                  {PERSONAL_QUESTIONS.map((q) => (
                    <div key={q.key}>
                      <p className="text-sm font-medium text-ink">{q.label}</p>
                      <div className="mt-2 grid grid-cols-1 gap-2">
                        {q.scale.map(([v, lbl]) => {
                          const active = scores[idx][q.key] === v;
                          return (
                            <button key={v} type="button" data-testid={`p-${idx}-${q.key}-${v}`} onClick={() => setScore(idx, q.key, v)}
                              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${active ? "border-brand-accent bg-brand/5 ring-1 ring-brand-accent" : "border-borderc bg-white hover:border-brand-accent/50"}`}>
                              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold ${active ? "border-brand bg-brand text-white" : "border-slate-300 text-slate-400"}`}>{v}</span>
                              <span className={active ? "text-ink font-medium" : "text-ink-soft"}>{lbl}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sticky bottom-0 z-30 border-t border-borderc bg-white/95 backdrop-blur">
          <div className="mx-auto max-w-2xl px-4 sm:px-0 py-3 flex justify-end">
            <Button onClick={submitPersonal} disabled={!personalComplete || saving} data-testid="personal-submit" className="h-11 bg-brand hover:bg-brand-hover px-6">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Lanjut ke Validasi Mendalam <ArrowRight className="ml-1 h-5 w-5" /></>}
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ---------- DEEP PROMPT ----------
  if (phase === "deepPrompt") {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl animate-fade-up" data-testid="deep-prompt">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Validasi Mendalam</h1>
          <p className="mt-3 text-base text-ink-soft leading-relaxed">IDEbisnis AI akan mengevaluasi kedua ide secara lebih mendalam berdasarkan profil dan penilaian personal Anda — potensi pasar, persaingan, efisiensi modal, risiko, dan cara mengujinya.</p>

          {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="deep-error">{error}</div>}

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {ideas.map((idea, idx) => {
              const p = val.personal?.[String(idx)];
              return (
                <div key={idx} className="rounded-2xl border border-borderc bg-white p-5" data-testid={`deep-summary-${idx}`}>
                  <span className="font-mono text-xs font-semibold text-brand-accent">IDE #{idx + 1}</span>
                  <h3 className="mt-1 font-heading text-base font-bold text-ink">{idea.name}</h3>
                  {p && <span className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${FIT_LABEL_STYLE[p.personal_fit_label] || MID}`}>{p.personal_fit_label}</span>}
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button onClick={runDeep} data-testid="run-deep-btn" className="h-12 bg-brand hover:bg-brand-hover px-6 text-base">
              <Sparkles className="mr-1.5 h-5 w-5" /> Jalankan Validasi Mendalam
            </Button>
            <Button variant="outline" onClick={() => setPhase("personal")} data-testid="edit-personal-btn" className="h-12 border-borderc px-6">Edit Validasi Personal</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ---------- RESULTS ----------
  const results = val.deep?.results || [];
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl animate-fade-up" data-testid="validation-results">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-brand transition-colors" data-testid="results-back">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </button>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-ink">Hasil Validasi</h1>
          <Button variant="outline" onClick={() => setPhase("deepPrompt")} data-testid="regenerate-deep" className="border-borderc gap-1.5"><RefreshCw className="h-4 w-4" /> Validasi Ulang</Button>
        </div>

        <div className="mt-5 space-y-6">
          {ideas.map((idea, idx) => {
            const p = val.personal?.[String(idx)];
            const d = results[idx] || {};
            return (
              <article key={idx} className="rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid={`result-card-${idx}`}>
                <span className="font-mono text-xs font-semibold text-brand-accent">IDE #{idx + 1}</span>
                <h2 className="mt-1 font-heading text-lg sm:text-xl font-bold text-ink" data-testid={`result-name-${idx}`}>{idea.name}</h2>

                {/* Personal fit */}
                <div className="mt-4 rounded-xl border border-borderc bg-surface p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-ink"><Heart className="h-4 w-4 text-brand" /><span className="text-sm font-semibold">Personal Fit</span></div>
                    {p && <span className={`rounded-full px-3 py-1 text-xs font-semibold ${FIT_LABEL_STYLE[p.personal_fit_label] || MID}`} data-testid={`personal-fit-label-${idx}`}>{p.personal_fit_label}</span>}
                  </div>
                  {p && (
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                      <MiniStat icon={Heart} label="Minat" value={p.interest_score} />
                      <MiniStat icon={Wallet} label="Modal" value={p.capital_score} />
                      <MiniStat icon={Clock} label="Waktu & Skill" value={p.skill_time_score} />
                    </div>
                  )}
                </div>

                {/* Business validation */}
                <div className="mt-4">
                  <h3 className="font-heading text-xs font-bold uppercase tracking-wide text-brand-accent">Validasi Bisnis</h3>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {METRICS.map(([field, label]) => (
                      <div key={field} className="rounded-xl border border-borderc bg-white p-3">
                        <div className="text-[11px] text-ink-muted leading-snug">{label}</div>
                        <span className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${toneFor(field, d[field])}`}>{d[field] || "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {d.capital_warning ? (
                  <div className="mt-4 flex items-start gap-2 rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-4 text-sm text-ink-soft" data-testid={`capital-warning-${idx}`}>
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brand-amber" /><span>{d.capital_warning}</span>
                  </div>
                ) : null}

                <ListBlock icon={MapPin} title="Potensi di Lokasi Anda" items={d.local_opportunities} />
                <ListBlock icon={ShieldAlert} title="Tantangan Utama" items={d.local_challenges} />
                <ListBlock icon={AlertTriangle} title="Risiko Terbesar" items={d.main_risks} />
                <ListBlock icon={HelpCircle} title="Asumsi Utama yang Perlu Diuji" items={d.key_assumptions} />

                {d.validation_test ? (
                  <div className="mt-4 rounded-xl border border-brand-accent/30 bg-brand/5 p-4">
                    <div className="flex items-center gap-1.5 text-brand"><FlaskConical className="h-4 w-4" /><span className="text-sm font-semibold">Cara Menguji Ide Ini</span></div>
                    <p className="mt-1 text-sm text-ink-soft leading-relaxed">{d.validation_test}</p>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

        <div className="mt-8 rounded-2xl border border-borderc bg-white p-5 text-center">
          <Button onClick={() => navigate("/comparison")} data-testid="compare-cta" className="h-12 bg-brand hover:bg-brand-hover px-8 text-base">
            <GitCompare className="mr-1.5 h-5 w-5" /> Bandingkan 2 Ide
          </Button>
          <p className="mt-3 text-xs text-ink-muted leading-relaxed">Hasil ini adalah penilaian awal berdasarkan model bisnis dan kondisi Anda, bukan data pasar terverifikasi. Uji dengan biaya kecil sebelum mengeluarkan modal besar.</p>
        </div>
      </div>
    </DashboardLayout>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-white border border-borderc py-2">
      <div className="flex items-center justify-center gap-1 text-ink-muted"><Icon className="h-3.5 w-3.5" /><span className="text-[11px]">{label}</span></div>
      <div className="mt-0.5 font-heading text-base font-extrabold text-brand">{value}<span className="text-xs font-medium text-ink-muted">/5</span></div>
    </div>
  );
}

function ListBlock({ icon: Icon, title, items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-4">
      <div className="flex items-center gap-1.5 text-ink"><Icon className="h-4 w-4 text-brand-accent" /><span className="text-sm font-semibold">{title}</span></div>
      <ul className="mt-1.5 list-disc pl-6 text-sm text-ink-soft space-y-1">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
